import { Fragment, useState } from 'react'
import { Dropdown } from 'react-bootstrap'
import { useSearchParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { getPartnerDisbursements, getPartnerPayouts, getPartnerTopUps, getPartnerStatement, exportPartnerTopUps, exportPartnerDisbursements, exportPartnerPayouts, exportPartnerStatement } from '../../services/LedgerService'
import { PERMISSION_SLUGS } from '../../utils/auth/permissionConstants'
import InfoPopover from './InfoPopover'
import { formatPhoneForDisplay, formatReceiverPartyForDisplay } from '../../utils/phoneFormat'
import { scrollToPageTop } from '../../utils/scrollToPageTop'

const UNDER_REVIEW_COPY = 'Milipot is verifying this top-up. You do not need to do anything. Do not retry.'
const PAYOUT_UNDER_REVIEW_COPY = 'Milipot is verifying this payout with M-PESA. You do not need to do anything. Do not retry.'

const TABS = {
    DISBURSEMENTS: 'disbursements',
    PAYOUTS:       'payouts',
    TOP_UPS:       'top-ups',
    STATEMENT:     'statement',
}

const EMPTY_PAYOUT_FILTERS       = { date_from: '', date_to: '', status: '', search: '' }
const EMPTY_DISBURSEMENT_FILTERS = { date_from: '', date_to: '', search: '', initiated_by_search: '' }
const EMPTY_TOP_UP_FILTERS       = { date_from: '', date_to: '', status: '', search: '' }
const EMPTY_STATEMENT_FILTERS    = { date_from: '', date_to: '', type: '' }

const formatAmount = (amount) =>
    `KES ${Number(amount).toLocaleString('en-KE', { minimumFractionDigits: 2 })}`

const formatDate = (dateString) =>
    new Date(dateString).toLocaleString('en-KE', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
    })

const describeStatementEntry = (entry) => {
    const description = entry.description || ''
    const receiverParty = entry.receiver_party
    if (!receiverParty || description.includes(' - ')) return description
    const parts = receiverParty.split(' - ')
    const name = parts.length > 1 ? parts.slice(1).join(' - ').trim() : receiverParty.trim()
    return name ? `${description} - ${name}` : description
}

const topUpStatusBadge = (topUp) => {
    switch (topUp?.status) {
        case 'successful':   return { cls: 'badge-success',   label: 'Successful' }
        case 'under_review': return { cls: 'badge-info',      label: 'Under Review' }
        case 'pending':      return { cls: 'badge-warning',   label: 'Pending' }
        case 'failed':       return { cls: 'badge-danger',    label: 'Failed' }
        default:             return { cls: 'badge-secondary', label: 'Unknown' }
    }
}

const payoutStatusBadge = (payout) => {
    switch (payout?.status) {
        case 'successful':   return { cls: 'badge-success',   label: 'Successful' }
        case 'under_review': return { cls: 'badge-info',      label: 'Under Review' }
        case 'pending':      return { cls: 'badge-warning',   label: 'Pending' }
        case 'failed':       return { cls: 'badge-danger',    label: 'Failed' }
        default:             return { cls: 'badge-secondary', label: 'Unknown' }
    }
}

const stripEmptyFilters = (filters) =>
    Object.fromEntries(Object.entries(filters).filter(([, v]) => v !== ''))


const DeletedBadge = () => (
    <span className="badge badge-rounded badge-danger ms-1" style={{ fontSize: '0.65rem' }}>deleted</span>
)

const TableLoadingRow = ({ colSpan }) => (
    <tr>
        <td colSpan={colSpan} className="text-center py-4">
            <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading…</span>
            </div>
        </td>
    </tr>
)

const TableErrorRow = ({ colSpan }) => (
    <tr>
        <td colSpan={colSpan} className="text-center py-4 text-danger">
            Failed to load records. Please try again.
        </td>
    </tr>
)

const TableEmptyRow = ({ colSpan, message }) => (
    <tr>
        <td colSpan={colSpan} className="text-center py-4">{message}</td>
    </tr>
)

const PaginationBar = ({ page, lastPage, isLoading, onPrev, onNext }) => {
    if (lastPage <= 1) return null
    return (
        <div className="d-flex justify-content-center align-items-center gap-3 px-3 py-2 border-top">
            <button
                type="button"
                className="btn btn-sm btn-link p-0 text-decoration-none"
                disabled={page <= 1 || isLoading}
                onClick={onPrev}
                aria-label="Previous page"
            >
                <i className="fa fa-angle-left me-1" />
                Prev
            </button>
            <span className="text-muted small">Page {page} of {lastPage}</span>
            <button
                type="button"
                className="btn btn-sm btn-link p-0 text-decoration-none"
                disabled={page >= lastPage || isLoading}
                onClick={onNext}
                aria-label="Next page"
            >
                Next
                <i className="fa fa-angle-right ms-1" />
            </button>
        </div>
    )
}

const FilterBar = ({ filters, showStatus, showUnderReviewStatus, showType, onChange, onClear, onExport, hasData }) => {
    const hasActiveFilter = Object.values(filters).some(v => v !== '')

    const statusOptions = [
        { value: '',           label: 'All statuses' },
        { value: 'successful', label: 'Successful' },
        { value: 'pending',    label: 'Pending' },
        ...(showUnderReviewStatus ? [{ value: 'reconciliation-required', label: 'Under Review' }] : []),
        { value: 'failed',     label: 'Failed' },
    ]
    const typeOptions = [
        { value: '',              label: 'All types' },
        { value: 'top_up',        label: 'Top-Up' },
        { value: 'disbursement',  label: 'Disbursement' },
    ]
    const currentStatusLabel = statusOptions.find(o => o.value === filters.status)?.label ?? 'All statuses'
    const currentTypeLabel   = typeOptions.find(o => o.value === filters.type)?.label ?? 'All types'
    const fromLabel = filters.date_from ? `From ${filters.date_from}` : 'From date'
    const toLabel   = filters.date_to   ? `To ${filters.date_to}`     : 'To date'

    return (
        <div className="mb-3">
            {'search' in filters && (
                <input
                    type="text"
                    className="form-control mb-2"
                    placeholder={'initiated_by_search' in filters ? 'Search by recipient' : 'Search'}
                    value={filters.search}
                    onChange={e => onChange('search', e.target.value)}
                />
            )}
            {'initiated_by_search' in filters && (
                <input
                    type="text"
                    className="form-control mb-2"
                    placeholder="Search by initiator"
                    value={filters.initiated_by_search}
                    onChange={e => onChange('initiated_by_search', e.target.value)}
                />
            )}
            <div className="d-flex flex-wrap gap-2 align-items-center">
                <Dropdown autoClose="outside">
                    <Dropdown.Toggle variant="outline-secondary" size="sm" className="d-inline-flex align-items-center" style={{ minWidth: '140px' }}>
                        <span className="flex-grow-1 text-start">{fromLabel}</span>
                    </Dropdown.Toggle>
                    <Dropdown.Menu className="p-2">
                        <input
                            type="date"
                            className="form-control form-control-sm"
                            value={filters.date_from}
                            onChange={e => onChange('date_from', e.target.value)}
                        />
                    </Dropdown.Menu>
                </Dropdown>
                <Dropdown autoClose="outside">
                    <Dropdown.Toggle variant="outline-secondary" size="sm" className="d-inline-flex align-items-center" style={{ minWidth: '140px' }}>
                        <span className="flex-grow-1 text-start">{toLabel}</span>
                    </Dropdown.Toggle>
                    <Dropdown.Menu className="p-2">
                        <input
                            type="date"
                            className="form-control form-control-sm"
                            value={filters.date_to}
                            onChange={e => onChange('date_to', e.target.value)}
                        />
                    </Dropdown.Menu>
                </Dropdown>
                {showStatus && (
                    <Dropdown>
                        <Dropdown.Toggle variant="outline-secondary" size="sm" className="d-inline-flex align-items-center" style={{ minWidth: '140px' }}>
                            <span className="flex-grow-1 text-start">{currentStatusLabel}</span>
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                            {statusOptions.map(option => (
                                <Dropdown.Item
                                    key={option.value || 'all'}
                                    active={filters.status === option.value}
                                    onClick={() => onChange('status', option.value)}
                                >
                                    {option.label}
                                </Dropdown.Item>
                            ))}
                        </Dropdown.Menu>
                    </Dropdown>
                )}
                {showType && (
                    <Dropdown>
                        <Dropdown.Toggle variant="outline-secondary" size="sm" className="d-inline-flex align-items-center" style={{ minWidth: '140px' }}>
                            <span className="flex-grow-1 text-start">{currentTypeLabel}</span>
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                            {typeOptions.map(option => (
                                <Dropdown.Item
                                    key={option.value || 'all'}
                                    active={filters.type === option.value}
                                    onClick={() => onChange('type', option.value)}
                                >
                                    {option.label}
                                </Dropdown.Item>
                            ))}
                        </Dropdown.Menu>
                    </Dropdown>
                )}
                {hasActiveFilter && (
                    <button
                        type="button"
                        className="btn btn-sm btn-link text-decoration-none p-0 ms-1"
                        onClick={onClear}
                    >
                        Clear
                    </button>
                )}
                <button
                    type="button"
                    className="btn btn-sm btn-link text-decoration-none p-0 ms-auto"
                    disabled={!hasData}
                    onClick={onExport}
                >
                    <i className="fa fa-download me-1" />
                    Export CSV
                </button>
            </div>
        </div>
    )
}

const PartnerLedger = () => {
    const partnerId           = useSelector((state) => state.auth.authUser?.partner_id)
    const userPermissionSlugs = useSelector((state) => state.auth.authUser?.all_permission_slugs ?? [])

    const [searchParams] = useSearchParams()
    const initialTab = Object.values(TABS).includes(searchParams.get('tab')) ? searchParams.get('tab') : TABS.STATEMENT

    const [activeTab, setActiveTab] = useState(initialTab)

    const [payoutsPage,        setPayoutsPage]        = useState(1)
    const [disbursementsPage,  setDisbursementsPage]  = useState(1)
    const [topUpsPage,         setTopUpsPage]         = useState(1)
    const [statementPage,      setStatementPage]      = useState(1)

    const [payoutsFilters,       setPayoutsFilters]       = useState(EMPTY_PAYOUT_FILTERS)
    const [disbursementsFilters, setDisbursementsFilters] = useState(EMPTY_DISBURSEMENT_FILTERS)
    const [topUpsFilters,        setTopUpsFilters]        = useState(EMPTY_TOP_UP_FILTERS)
    const [statementFilters,     setStatementFilters]     = useState(EMPTY_STATEMENT_FILTERS)

    const canViewLedger = userPermissionSlugs.includes(PERMISSION_SLUGS.VIEW_PARTNER_STATEMENTS)

    const handlePayoutsFilterChange = (key, value) => {
        setPayoutsFilters(prev => ({ ...prev, [key]: value }))
        setPayoutsPage(1)
    }

    const handleDisbursementsFilterChange = (key, value) => {
        setDisbursementsFilters(prev => ({ ...prev, [key]: value }))
        setDisbursementsPage(1)
    }

    const handleTopUpsFilterChange = (key, value) => {
        setTopUpsFilters(prev => ({ ...prev, [key]: value }))
        setTopUpsPage(1)
    }

    const handleStatementFilterChange = (key, value) => {
        setStatementFilters(prev => ({ ...prev, [key]: value }))
        setStatementPage(1)
    }

    const payoutsQuery = useQuery({
        queryKey: ['partner-payouts', partnerId, payoutsPage, payoutsFilters],
        queryFn: () => getPartnerPayouts(partnerId, payoutsPage, stripEmptyFilters(payoutsFilters)),
        placeholderData: keepPreviousData,
        staleTime: 0,
        enabled: canViewLedger && !!partnerId && activeTab === TABS.PAYOUTS,
    })

    const disbursementsQuery = useQuery({
        queryKey: ['partner-disbursements', partnerId, disbursementsPage, disbursementsFilters],
        queryFn: () => getPartnerDisbursements(partnerId, disbursementsPage, stripEmptyFilters(disbursementsFilters)),
        placeholderData: keepPreviousData,
        staleTime: 0,
        enabled: canViewLedger && !!partnerId && activeTab === TABS.DISBURSEMENTS,
    })

    const topUpsQuery = useQuery({
        queryKey: ['partner-top-ups', partnerId, topUpsPage, topUpsFilters],
        queryFn: () => getPartnerTopUps(partnerId, topUpsPage, stripEmptyFilters(topUpsFilters)),
        placeholderData: keepPreviousData,
        staleTime: 0,
        enabled: canViewLedger && !!partnerId && activeTab === TABS.TOP_UPS,
    })

    const statementQuery = useQuery({
        queryKey: ['partner-statement', partnerId, statementPage, statementFilters],
        queryFn: () => getPartnerStatement(partnerId, statementPage, stripEmptyFilters(statementFilters)),
        placeholderData: keepPreviousData,
        staleTime: 0,
        enabled: canViewLedger && !!partnerId && activeTab === TABS.STATEMENT,
    })

    if (!canViewLedger) {
        return (
            <div className="row">
                <div className="col-12">
                    <div className="card">
                        <div className="card-body text-center py-5">
                            <p className="text-muted mb-0">You do not have permission to view this page.</p>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    const payouts     = payoutsQuery.data?.data?.data ?? []
    const payoutsLast = payoutsQuery.data?.data?.meta?.last_page ?? 1

    const disbursements     = disbursementsQuery.data?.data?.data ?? []
    const disbursementsLast = disbursementsQuery.data?.data?.meta?.last_page ?? 1

    const topUps     = topUpsQuery.data?.data?.data ?? []
    const topUpsLast = topUpsQuery.data?.data?.meta?.last_page ?? 1

    const statementData    = statementQuery.data?.data
    const statementEntries = statementData?.entries ?? []
    const statementLast    = statementData?.meta?.last_page ?? 1

    const exportPayoutsCsv = async () => {
        try {
            const response = await exportPartnerPayouts(partnerId, stripEmptyFilters(payoutsFilters))
            const url  = URL.createObjectURL(new Blob([response.data]))
            const link = document.createElement('a')
            link.href     = url
            link.download = 'payouts.csv'
            link.click()
            URL.revokeObjectURL(url)
        } catch (e) {
            console.error('Payouts export failed', e)
        }
    }

    const exportDisbursementsCsv = async () => {
        try {
            const response = await exportPartnerDisbursements(partnerId, stripEmptyFilters(disbursementsFilters))
            const url  = URL.createObjectURL(new Blob([response.data]))
            const link = document.createElement('a')
            link.href     = url
            link.download = 'disbursements.csv'
            link.click()
            URL.revokeObjectURL(url)
        } catch (e) {
            console.error('Disbursements export failed', e)
        }
    }

    const exportTopUpsCsv = async () => {
        try {
            const response = await exportPartnerTopUps(partnerId, stripEmptyFilters(topUpsFilters))
            const url  = URL.createObjectURL(new Blob([response.data]))
            const link = document.createElement('a')
            link.href     = url
            link.download = 'top-ups.csv'
            link.click()
            URL.revokeObjectURL(url)
        } catch (e) {
            console.error('Top-ups export failed', e)
        }
    }

    const exportStatementCsv = async () => {
        try {
            const response = await exportPartnerStatement(partnerId, stripEmptyFilters(statementFilters))
            const url  = URL.createObjectURL(new Blob([response.data]))
            const link = document.createElement('a')
            link.href     = url
            link.download = 'vault-statement.csv'
            link.click()
            URL.revokeObjectURL(url)
        } catch (e) {
            console.error('Statement export failed', e)
        }
    }

    return (
        <Fragment>
            <div className="row">
                <div className="col-12">
                    <div className="card">
                        <div className="card-header">
                            <h4 className="card-title mb-0">Ledger</h4>
                        </div>
                        <div className="card-body">
                            <ul className="nav nav-tabs mb-3">
                                <li className="nav-item">
                                    <button
                                        className={`nav-link ${activeTab === TABS.STATEMENT ? 'active' : ''}`}
                                        onClick={() => setActiveTab(TABS.STATEMENT)}
                                    >
                                        Statement
                                    </button>
                                </li>
                                <li className="nav-item">
                                    <button
                                        className={`nav-link ${activeTab === TABS.PAYOUTS ? 'active' : ''}`}
                                        onClick={() => setActiveTab(TABS.PAYOUTS)}
                                    >
                                        Payouts
                                    </button>
                                </li>
                                <li className="nav-item">
                                    <button
                                        className={`nav-link ${activeTab === TABS.DISBURSEMENTS ? 'active' : ''}`}
                                        onClick={() => setActiveTab(TABS.DISBURSEMENTS)}
                                    >
                                        Disbursements
                                    </button>
                                </li>
                                <li className="nav-item">
                                    <button
                                        className={`nav-link ${activeTab === TABS.TOP_UPS ? 'active' : ''}`}
                                        onClick={() => setActiveTab(TABS.TOP_UPS)}
                                    >
                                        Top-Ups
                                    </button>
                                </li>
                            </ul>

                            {activeTab === TABS.PAYOUTS && (
                                <div>
                                    <FilterBar
                                        filters={payoutsFilters}
                                        showStatus={true}
                                        showUnderReviewStatus={true}
                                        onChange={handlePayoutsFilterChange}
                                        onClear={() => { setPayoutsFilters(EMPTY_PAYOUT_FILTERS); setPayoutsPage(1) }}
                                        onExport={exportPayoutsCsv}
                                        hasData={payouts.length > 0}
                                    />
                                    <div>
                                        <table className="table verticle-middle mb-0" style={{ tableLayout: 'fixed', width: '100%' }}>
                                            <thead className="thead-lg-only">
                                                <tr>
                                                    <th scope="col" style={{ width: '18%' }}>User</th>
                                                    <th scope="col" style={{ width: '14%' }}>Amount</th>
                                                    <th scope="col" style={{ width: '24%' }}>Recipient</th>
                                                    <th scope="col" style={{ width: '16%' }}>Mpesa ID</th>
                                                    <th scope="col" style={{ width: '12%' }}>Status</th>
                                                    <th scope="col" style={{ width: '16%' }}>Date</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {payoutsQuery.status === 'pending' ? (
                                                    <TableLoadingRow colSpan={6} />
                                                ) : payoutsQuery.error ? (
                                                    <TableErrorRow colSpan={6} />
                                                ) : payouts.length === 0 ? (
                                                    <TableEmptyRow colSpan={6} message="No payouts recorded yet." />
                                                ) : (
                                                    payouts.map((item, index) => {
                                                        const badge = payoutStatusBadge(item)
                                                        return (
                                                            <tr key={index}>
                                                                <td>
                                                                    <div className="d-lg-none d-flex justify-content-between align-items-start gap-3">
                                                                        <div style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                                                                            <div className="fw-semibold text-nowrap">
                                                                                {item.amount != null
                                                                                    ? formatAmount(item.amount)
                                                                                    : <span className="text-muted fst-italic">Pending</span>}
                                                                            </div>
                                                                            <div className="fw-semibold mt-1">
                                                                                {item.user ? (
                                                                                    <>
                                                                                        {item.user.name}
                                                                                        {item.user.is_deleted && <DeletedBadge />}
                                                                                    </>
                                                                                ) : '-'}
                                                                            </div>
                                                                            <div className="text-muted small mt-1">To {item.receiver_party ? formatReceiverPartyForDisplay(item.receiver_party) : '-'}</div>
                                                                            <div className="text-muted small">Receipt {item.post_mpesa_receipt_number || '-'}</div>
                                                                            <div className="text-muted small">{formatDate(item.post_mpesa_transaction_time ?? item.created_at)}</div>
                                                                        </div>
                                                                        <div className="text-end flex-shrink-0">
                                                                            <div className="d-inline-flex align-items-center gap-2">
                                                                                <span className={`badge badge-rounded ${badge.cls}`}>
                                                                                    {badge.label}
                                                                                </span>
                                                                                {item.status === 'under_review' && (
                                                                                    <InfoPopover
                                                                                        id={`payout-under-review-mobile-${item.id ?? index}`}
                                                                                        body={PAYOUT_UNDER_REVIEW_COPY}
                                                                                        ariaLabel="What does Under Review mean?"
                                                                                    />
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                    <span className="d-none d-lg-inline text-nowrap">
                                                                        {item.user ? (
                                                                            <>
                                                                                {item.user.name}
                                                                                {item.user.is_deleted && <DeletedBadge />}
                                                                            </>
                                                                        ) : '-'}
                                                                    </span>
                                                                </td>
                                                                <td className="d-none d-lg-table-cell text-nowrap">
                                                                    {item.amount != null
                                                                        ? formatAmount(item.amount)
                                                                        : <span className="text-muted fst-italic">Pending</span>}
                                                                </td>
                                                                <td className="d-none d-lg-table-cell" style={{ maxWidth: '200px', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                                                    {item.receiver_party ? formatReceiverPartyForDisplay(item.receiver_party) : '-'}
                                                                </td>
                                                                <td className="d-none d-lg-table-cell text-nowrap">{item.post_mpesa_receipt_number || '-'}</td>
                                                                <td className="d-none d-lg-table-cell">
                                                                    <span className={`badge badge-rounded ${badge.cls}`}>
                                                                        {badge.label}
                                                                    </span>
                                                                    {item.status === 'under_review' && (
                                                                        <InfoPopover
                                                                            id={`payout-under-review-${item.id ?? index}`}
                                                                            body={PAYOUT_UNDER_REVIEW_COPY}
                                                                            ariaLabel="What does Under Review mean?"
                                                                        />
                                                                    )}
                                                                </td>
                                                                <td className="d-none d-lg-table-cell text-nowrap">{formatDate(item.post_mpesa_transaction_time ?? item.created_at)}</td>
                                                            </tr>
                                                        )
                                                    })
                                                )}
                                            </tbody>
                                        </table>
                                        <PaginationBar
                                            page={payoutsPage}
                                            lastPage={payoutsLast}
                                            isLoading={payoutsQuery.isFetching}
                                            onPrev={() => { setPayoutsPage(p => p - 1); scrollToPageTop(); }}
                                            onNext={() => { setPayoutsPage(p => p + 1); scrollToPageTop(); }}
                                        />
                                    </div>
                                </div>
                            )}

                            {activeTab === TABS.DISBURSEMENTS && (
                                <div>
                                    <FilterBar
                                        filters={disbursementsFilters}
                                        showStatus={false}
                                        onChange={handleDisbursementsFilterChange}
                                        onClear={() => { setDisbursementsFilters(EMPTY_DISBURSEMENT_FILTERS); setDisbursementsPage(1) }}
                                        onExport={exportDisbursementsCsv}
                                        hasData={disbursements.length > 0}
                                    />
                                    <div>
                                        <table className="table verticle-middle mb-0" style={{ tableLayout: 'fixed', width: '100%' }}>
                                            <thead className="thead-lg-only">
                                                <tr>
                                                    <th scope="col" style={{ width: '30%' }}>User</th>
                                                    <th scope="col" style={{ width: '25%' }}>Initiated by</th>
                                                    <th scope="col" style={{ width: '20%' }}>Amount</th>
                                                    <th scope="col" style={{ width: '25%' }}>Date</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {disbursementsQuery.status === 'pending' ? (
                                                    <TableLoadingRow colSpan={4} />
                                                ) : disbursementsQuery.error ? (
                                                    <TableErrorRow colSpan={4} />
                                                ) : disbursements.length === 0 ? (
                                                    <TableEmptyRow colSpan={4} message="No disbursements recorded yet." />
                                                ) : (
                                                    disbursements.map((item, index) => (
                                                        <tr key={index}>
                                                            <td style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                                                <div className="d-lg-none d-flex justify-content-between align-items-start gap-3">
                                                                    <div style={{ minWidth: 0 }}>
                                                                        <div className="fw-semibold">
                                                                            {item.user ? (
                                                                                <>
                                                                                    {item.user.name}
                                                                                    {item.user.is_deleted && <DeletedBadge />}
                                                                                </>
                                                                            ) : '-'}
                                                                        </div>
                                                                        <div className="text-muted small mt-1">
                                                                            Initiated by{' '}
                                                                            {item.initiated_by ? (
                                                                                <>
                                                                                    {item.initiated_by.name}
                                                                                    {item.initiated_by.is_deleted && <DeletedBadge />}
                                                                                </>
                                                                            ) : '-'}
                                                                        </div>
                                                                        <div className="text-muted small mt-1">{formatDate(item.created_at)}</div>
                                                                    </div>
                                                                    <div className="fw-semibold text-nowrap flex-shrink-0">
                                                                        {formatAmount(item.amount)}
                                                                    </div>
                                                                </div>
                                                                <span className="d-none d-lg-inline fw-semibold">
                                                                    {item.user ? (
                                                                        <>
                                                                            {item.user.name}
                                                                            {item.user.is_deleted && <DeletedBadge />}
                                                                        </>
                                                                    ) : '-'}
                                                                </span>
                                                            </td>
                                                            <td className="d-none d-lg-table-cell" style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                                                {item.initiated_by ? (
                                                                    <>
                                                                        {item.initiated_by.name}
                                                                        {item.initiated_by.is_deleted && <DeletedBadge />}
                                                                    </>
                                                                ) : <span className="text-muted">-</span>}
                                                            </td>
                                                            <td className="d-none d-lg-table-cell text-nowrap">{formatAmount(item.amount)}</td>
                                                            <td className="d-none d-lg-table-cell text-nowrap">{formatDate(item.created_at)}</td>
                                                        </tr>
                                                    ))
                                                )}
                                            </tbody>
                                        </table>
                                        <PaginationBar
                                            page={disbursementsPage}
                                            lastPage={disbursementsLast}
                                            isLoading={disbursementsQuery.isFetching}
                                            onPrev={() => { setDisbursementsPage(p => p - 1); scrollToPageTop(); }}
                                            onNext={() => { setDisbursementsPage(p => p + 1); scrollToPageTop(); }}
                                        />
                                    </div>
                                </div>
                            )}

                            {activeTab === TABS.TOP_UPS && (
                                <div>
                                    <FilterBar
                                        filters={topUpsFilters}
                                        showStatus={true}
                                        showUnderReviewStatus={true}
                                        onChange={handleTopUpsFilterChange}
                                        onClear={() => { setTopUpsFilters(EMPTY_TOP_UP_FILTERS); setTopUpsPage(1) }}
                                        onExport={exportTopUpsCsv}
                                        hasData={topUps.length > 0}
                                    />
                                    <div>
                                        <table className="table verticle-middle mb-0" style={{ tableLayout: 'fixed', width: '100%' }}>
                                            <thead className="thead-lg-only">
                                                <tr>
                                                    <th scope="col" style={{ width: '18%' }}>Amount</th>
                                                    <th scope="col" style={{ width: '22%' }}>Source</th>
                                                    <th scope="col" style={{ width: '24%' }}>Mpesa ID</th>
                                                    <th scope="col" style={{ width: '14%' }}>Status</th>
                                                    <th scope="col" style={{ width: '22%' }}>Date</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {topUpsQuery.status === 'pending' ? (
                                                    <TableLoadingRow colSpan={5} />
                                                ) : topUpsQuery.error ? (
                                                    <TableErrorRow colSpan={5} />
                                                ) : topUps.length === 0 ? (
                                                    <TableEmptyRow colSpan={5} message="No top-ups recorded yet." />
                                                ) : (
                                                    topUps.map((item, index) => {
                                                        const badge = topUpStatusBadge(item)
                                                        return (
                                                            <tr key={index}>
                                                                <td>
                                                                    <div className="d-lg-none d-flex justify-content-between align-items-start gap-3">
                                                                        <div style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                                                                            <div className="fw-semibold text-nowrap">
                                                                                {item.amount != null
                                                                                    ? formatAmount(item.amount)
                                                                                    : <span className="text-muted fst-italic">Pending</span>}
                                                                            </div>
                                                                            <div className="text-muted small mt-1">From {item.source_phone_number ? formatPhoneForDisplay(item.source_phone_number) : '-'}</div>
                                                                            <div className="text-muted small">Receipt {item.post_mpesa_receipt_number || '-'}</div>
                                                                            <div className="text-muted small">{formatDate(item.post_mpesa_transaction_date ?? item.created_at)}</div>
                                                                        </div>
                                                                        <div className="text-end flex-shrink-0">
                                                                            <div className="d-inline-flex align-items-center gap-2">
                                                                                <span className={`badge badge-rounded ${badge.cls}`}>
                                                                                    {badge.label}
                                                                                </span>
                                                                                {item.status === 'under_review' && (
                                                                                    <InfoPopover
                                                                                        id={`top-up-under-review-mobile-${item.id ?? index}`}
                                                                                        body={UNDER_REVIEW_COPY}
                                                                                        ariaLabel="What does Under Review mean?"
                                                                                    />
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                    <span className="d-none d-lg-inline text-nowrap fw-semibold">
                                                                        {item.amount != null
                                                                            ? formatAmount(item.amount)
                                                                            : <span className="text-muted fst-italic">Pending</span>}
                                                                    </span>
                                                                </td>
                                                                <td className="d-none d-lg-table-cell text-nowrap">{item.source_phone_number ? formatPhoneForDisplay(item.source_phone_number) : '-'}</td>
                                                                <td className="d-none d-lg-table-cell text-nowrap">{item.post_mpesa_receipt_number || '-'}</td>
                                                                <td className="d-none d-lg-table-cell">
                                                                    <span className={`badge badge-rounded ${badge.cls}`}>
                                                                        {badge.label}
                                                                    </span>
                                                                    {item.status === 'under_review' && (
                                                                        <InfoPopover
                                                                            id={`top-up-under-review-${item.id ?? index}`}
                                                                            body={UNDER_REVIEW_COPY}
                                                                            ariaLabel="What does Under Review mean?"
                                                                        />
                                                                    )}
                                                                </td>
                                                                <td className="d-none d-lg-table-cell text-nowrap">{formatDate(item.post_mpesa_transaction_date ?? item.created_at)}</td>
                                                            </tr>
                                                        )
                                                    })
                                                )}
                                            </tbody>
                                        </table>
                                        <PaginationBar
                                            page={topUpsPage}
                                            lastPage={topUpsLast}
                                            isLoading={topUpsQuery.isFetching}
                                            onPrev={() => { setTopUpsPage(p => p - 1); scrollToPageTop(); }}
                                            onNext={() => { setTopUpsPage(p => p + 1); scrollToPageTop(); }}
                                        />
                                    </div>
                                </div>
                            )}

                            {activeTab === TABS.STATEMENT && (
                                <div>
                                    {statementData && (
                                        <div className="mb-2">
                                            <span className="text-muted small d-block">{statementData.account_name}</span>
                                            <span className="fw-bold fs-5">{formatAmount(statementData.current_balance ?? '0')}</span>
                                            <span className="text-muted small ms-1">current balance</span>
                                        </div>
                                    )}
                                    <FilterBar
                                        filters={statementFilters}
                                        showStatus={false}
                                        showType={true}
                                        onChange={handleStatementFilterChange}
                                        onClear={() => { setStatementFilters(EMPTY_STATEMENT_FILTERS); setStatementPage(1) }}
                                        onExport={exportStatementCsv}
                                        hasData={statementEntries.length > 0}
                                    />
                                    <div>
                                        <table className="table verticle-middle mb-0" style={{ tableLayout: 'fixed', width: '100%' }}>
                                            <thead>
                                                <tr>
                                                    <th scope="col" className="d-none d-lg-table-cell" style={{ width: '14%' }}>Date</th>
                                                    <th scope="col" className="d-none d-lg-table-cell" style={{ width: '12%' }}>Type</th>
                                                    <th scope="col" style={{ width: '42%' }}>Description</th>
                                                    <th scope="col" className="d-none d-lg-table-cell" style={{ width: '18%' }}>Reference</th>
                                                    <th scope="col" className="d-none d-lg-table-cell text-end" style={{ width: '14%' }}>Amount</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {statementQuery.status === 'pending' ? (
                                                    <TableLoadingRow colSpan={5} />
                                                ) : statementQuery.error ? (
                                                    <TableErrorRow colSpan={5} />
                                                ) : statementEntries.length === 0 ? (
                                                    <TableEmptyRow colSpan={5} message="No entries recorded yet." />
                                                ) : (
                                                    statementEntries.map((entry, index) => (
                                                        <tr key={index}>
                                                            <td className="d-none d-lg-table-cell text-nowrap">{formatDate(entry.date)}</td>
                                                            <td className="d-none d-lg-table-cell text-nowrap">
                                                                {entry.type === 'top_up' ? 'Top-Up' : 'Disbursement'}
                                                            </td>
                                                            <td style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                                                <div className="d-lg-none d-flex justify-content-between align-items-start gap-3">
                                                                    <div style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                                                                        <div>
                                                                            <span>{describeStatementEntry(entry)}</span>
                                                                            {entry.recipient && (
                                                                                <span className={`ms-1 ${entry.recipient_deleted ? 'text-muted' : ''}`}>
                                                                                    - {entry.recipient}
                                                                                    {entry.recipient_deleted && <DeletedBadge />}
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                        <div className="text-muted small mt-1" style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                                                            {formatDate(entry.date)} · {entry.type === 'top_up' ? 'Top-Up' : 'Disbursement'}
                                                                            {entry.reference && ` · ${entry.reference}`}
                                                                        </div>
                                                                    </div>
                                                                    <div className={`text-end flex-shrink-0 fw-medium text-nowrap ${entry.direction === 'in' ? 'text-success' : 'text-danger'}`}>
                                                                        {entry.direction === 'in' ? '+' : '-'}{formatAmount(entry.amount)}
                                                                    </div>
                                                                </div>
                                                                <span className="d-none d-lg-inline">
                                                                    {describeStatementEntry(entry)}
                                                                    {entry.recipient && (
                                                                        <span className={`ms-1 ${entry.recipient_deleted ? 'text-muted' : ''}`}>
                                                                            - {entry.recipient}
                                                                            {entry.recipient_deleted && <DeletedBadge />}
                                                                        </span>
                                                                    )}
                                                                </span>
                                                            </td>
                                                            <td className="d-none d-lg-table-cell text-nowrap text-muted small">{entry.reference || '-'}</td>
                                                            <td className={`d-none d-lg-table-cell text-end text-nowrap fw-medium ${entry.direction === 'in' ? 'text-success' : 'text-danger'}`}>
                                                                {entry.direction === 'in' ? '+' : '-'}{formatAmount(entry.amount)}
                                                            </td>
                                                        </tr>
                                                    ))
                                                )}
                                            </tbody>
                                        </table>
                                        <PaginationBar
                                            page={statementPage}
                                            lastPage={statementLast}
                                            isLoading={statementQuery.isFetching}
                                            onPrev={() => { setStatementPage(p => p - 1); scrollToPageTop(); }}
                                            onNext={() => { setStatementPage(p => p + 1); scrollToPageTop(); }}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </Fragment>
    )
}

export default PartnerLedger
